import { Box, ClickAwayListener } from "@mui/material";
import { Minus, Plus } from "@pmag/icons";
import { IconButton, Tooltip } from "@pmag/ui-components";
import { type GQLComponentSubGroupGenericFragment, type GQLSparePartHotspot, type GQLSubGroupDetailSparePartFragment } from "@src/graphql.generated";
import { SmallListItem } from "@src/pages/subGroupDetail/subgroupDetailsListSection/smallListItem/SmallListItem";
import { type FunctionComponent, useRef, useState } from "react";
import { type ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import { getSparePartByHotspot } from "../explosionGraphicModal/ExplosionGraphicModal.utils";
import {
    BoxActions,
    BoxTooltip,
    BoxZoomContainer,
    ExplosionGraphicHotspot,
    ExplosionGraphicHotspotGhost,
    ExplosionGraphicHotspots,
    ExplosionGraphicImage,
    IconButtonRightBorder,
} from "./ExplosionGraphicSmall.styles";

export const dataTestId = {
    modal: "sparePartsFinder.subgroupPage.explosionGraphicModal.modal",
    hotspots: "sparePartsFinder.subgroupPage.explosionGraphicModal.hotspots",
    hotspot: "sparePartsFinder.subgroupPage.explosionGraphicModal.hotspot",
    transformComponent: "sparePartsFinder.subgroupPage.explosionGraphicModal.transformComponent",
    image: "sparePartsFinder.subgroupPage.explosionGraphicModal.image",
    zoomInButton: "sparePartsFinder.subgroupPage.explosionGraphicModal.button.zoomIn",
    zoomOutButton: "sparePartsFinder.subgroupPage.explosionGraphicModal.button.zoomOut",
    selectSparePartButton: "sparePartsFinder.subgroupPage.explosionGraphicModal.button.selectSparePart",
    availabilityStatus: "sparePartsFinder.subgroupPage.explosionGraphicModal.availabilityStatus",
};

export interface ExplosionGraphicModalProps {
    brand: string;
    clientWishlist: string[];
    componentGroupItem: GQLComponentSubGroupGenericFragment;
    hotspots: GQLSparePartHotspot[];
    modelId: string;
    spareParts: GQLSubGroupDetailSparePartFragment[];
}

interface HTMLImageElementWithLoadEvent extends HTMLImageElement {
    onerror: () => void;
}

export const ExplosionGraphicSmall: FunctionComponent<ExplosionGraphicModalProps> = ({
    brand,
    modelId,
    componentGroupItem,
    hotspots,
    spareParts,
    clientWishlist,
}) => {
    const zoomWrapper = useRef<ReactZoomPanPinchRef>(null);
    const boxZoomContainer = useRef<HTMLElement>(null);
    const boxZoomContent = useRef<HTMLElement>(null);
    const hotspotsWithSparePart = hotspots.map((hotspot) => {
        return {
            ...hotspot,
            ...{
                sparePart: getSparePartByHotspot(spareParts, hotspot),
            },
        };
    });
    const hotspotsWithSparePartDefined = hotspotsWithSparePart.filter((hotspot) => hotspot.sparePart);

    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const image = useRef<HTMLImageElementWithLoadEvent>(null);

    const [tooltipsOpen, setTooltipsOpen] = useState(hotspotsWithSparePartDefined.map(() => false));

    const checkImage = () => {
        if (boxZoomContainer.current && boxZoomContent.current && zoomWrapper.current) {
            const boxZoomContainerBoundingClientRect = boxZoomContainer.current.getBoundingClientRect();
            const boxZoomContentBoundingClientRect = boxZoomContent.current.getBoundingClientRect();
            const { width: widthBoxZoomContainer } = boxZoomContainerBoundingClientRect;
            const { width: widthBoxZoomContent } = boxZoomContentBoundingClientRect;
            zoomWrapper.current.setTransform(0, 0, widthBoxZoomContainer / widthBoxZoomContent, 0);
        }
        setImageLoaded(true);
    };

    const replaceImage = () => {
        if (!imageLoaded) {
            image.current?.removeEventListener("load", checkImage);
            image.current?.removeEventListener("error", replaceImage);
            image.current?.setAttribute("src", componentGroupItem.explosionAssetMediumResolution ?? "");
            setImageLoaded(true);
        }
    };

    const handleClick = (index: number) => {
        const updated = tooltipsOpen.map((_, i) => i === index);
        setTooltipsOpen(updated);
    };

    const closeTooltips = () => {
        const allClosed = tooltipsOpen.map(() => false);
        setTooltipsOpen(allClosed);
    };

    const assetPath = componentGroupItem.explosionAssetHighResolution ?? componentGroupItem.explosionAssetMediumResolution;

    return (
        <BoxZoomContainer ref={boxZoomContainer}>
            <TransformWrapper minScale={0.2} maxScale={2} doubleClick={{ step: 0.4 }} ref={zoomWrapper}>
                {({ zoomIn, zoomOut }) => (
                    <>
                        <BoxActions>
                            <IconButtonRightBorder variant="transparent" onClick={() => zoomIn()} data-testid={dataTestId.zoomInButton}>
                                <Plus />
                            </IconButtonRightBorder>

                            <IconButton variant="transparent" onClick={() => zoomOut()} data-testid={dataTestId.zoomOutButton}>
                                <Minus />
                            </IconButton>
                        </BoxActions>

                        <TransformComponent contentProps={{ role: "group" }}>
                            <Box ref={boxZoomContent}>
                                <ExplosionGraphicHotspots data-testid={dataTestId.hotspots}>
                                    {hotspotsWithSparePartDefined.map((hotspot, index) => (
                                        <ExplosionGraphicHotspot
                                            left={`${hotspot.startX * 100}%`}
                                            top={`${(1 - hotspot.endY) * 100}%`}
                                            width={`${(hotspot.endX - hotspot.startX) * 100}%`}
                                            height={`${(hotspot.endY - hotspot.startY) * 100}%`}
                                            key={hotspot.id}
                                            data-testid={dataTestId.hotspot}
                                            onClick={() => handleClick(index)}
                                        >
                                            {hotspot.sparePart && (
                                                <Tooltip
                                                    open={tooltipsOpen[index]}
                                                    content={
                                                        <ClickAwayListener onClickAway={closeTooltips}>
                                                            <BoxTooltip>
                                                                <SmallListItem
                                                                    brand={brand}
                                                                    modelId={modelId}
                                                                    sparePart={hotspot.sparePart}
                                                                    showBorderBottom={false}
                                                                    onClose={closeTooltips}
                                                                    clientWishlist={clientWishlist}
                                                                />
                                                            </BoxTooltip>
                                                        </ClickAwayListener>
                                                    }
                                                >
                                                    <ExplosionGraphicHotspotGhost />
                                                </Tooltip>
                                            )}
                                        </ExplosionGraphicHotspot>
                                    ))}
                                </ExplosionGraphicHotspots>

                                {assetPath && (
                                    <ExplosionGraphicImage
                                        src={assetPath}
                                        onLoad={checkImage}
                                        onError={replaceImage}
                                        data-testid={dataTestId.image}
                                        ref={image}
                                    />
                                )}
                            </Box>
                        </TransformComponent>
                    </>
                )}
            </TransformWrapper>
        </BoxZoomContainer>
    );
};
